import {
    AutoFixHighOutlined,
    Dashboard,
    EngineeringOutlined,
    HomeOutlined,
    Info,
    LocalMallOutlined,
    MuseumOutlined,
    RequestQuoteOutlined,
    PrecisionManufacturingOutlined,
    SellOutlined,
    ShoppingBasketOutlined, SnippetFolderOutlined,
    Verified,
    VpnKeyRounded,
} from '@mui/icons-material';
import { SecondaryNavBars, SelectBoxOption } from "../interfaces";
import { AppRoutes } from "../router/routes";
import { MasterPath } from '../screens/documentTemplates/sequenceNumbering/util';
import { MainModule, PERMISSIONS } from '../router/permission';
import { HasAccess } from '../router/authorization';
import { parseJwt } from './helperFunctions';
import { L4 } from '../screens/userManagement/utils';

// Submenu plugins
import align from "suneditor/src/plugins/submenu/align";
import font from "suneditor/src/plugins/submenu/font";
import fontColor from "suneditor/src/plugins/submenu/fontColor";
import fontSize from "suneditor/src/plugins/submenu/fontSize";
import hiliteColor from "suneditor/src/plugins/submenu/hiliteColor";
import horizontalRule from "suneditor/src/plugins/submenu/horizontalRule";
import list from "suneditor/src/plugins/submenu/list";
import paragraphStyle from "suneditor/src/plugins/submenu/paragraphStyle";
import table from "suneditor/src/plugins/submenu/table";
import textStyle from "suneditor/src/plugins/submenu/textStyle";

// Dialog plugins
import image from "suneditor/src/plugins/dialog/image";
import link from "suneditor/src/plugins/dialog/link";
import video from "suneditor/src/plugins/dialog/video";
import audio from "suneditor/src/plugins/dialog/audio";
import math from "suneditor/src/plugins/dialog/math";
import mention from "suneditor/src/plugins/dialog/mention";

import blockquote from "suneditor/src/plugins/command/blockquote";

/**
 * Object with role as key and value, which is used for 
 * comparison of role in different place.
 */
export const UserRoles = {
    admin: "admin",
    user: "user"
};

/**
 * Object which has the proper name of all the role
 * used in the application.
 */
export let UserRolesName = {
    [UserRoles.admin]: "Admin",
    [UserRoles.user]: "User"
};

/**
 * Object which has the different themes used in 
 * the application.
 */
export let Themes = {
    default: "default",
    dark: "dark",
};

/**
 * Object which has the different props for the Alert Component (/src/component/alert) 
 * which is used via AlertContext (/src/contexts) and provided at /src/App.alert.js.
 */
export let AlertProps = {
    vertical: {
        top: "top",
        bottom: "bottom",
    },
    horizontal: {
        left: "left",
        right: "right",
        center: "center",
    },
    severity: {
        success: "success",
        error: "error",
        warning: "warning",
        info: "info",
    },
};

/**
 * Object which has the different props for the Drawer Component (/src/App.drawer.js) 
 * which is used via DrawerContext (/src/contexts) and provided at /src/App.drawer.js.
 */
export const DrawerProps = {
    direction: {
        top: "top",
        bottom: "bottom",
        left: "left",
        right: "right",
    },
    variant: {
        permanent: "permanent",
        persistent: "persistent",
        temporary: "temporary",
    },
};

/**
 * Object has the key and value pair of all the keys which 
 * are used to store some values in the local storage.
 */
export let LocalStorageKeys = {
    authToken: "auth_token",
    version: "version",
    name: "name",
    nounRecentSearch: "nounRecentSearch",
    template: "template",
    keywordRecentSearch: "keywordRecentSearch",
    snNounRecentSearch: "snNounRecentSearch",
    logoutTimer: "logoutTimer",
    timeout: "timeout",
    locationStatePaths: "paths",
    resizeWidthBOM: "resizeWidthBOM",
    resizeWidthBOMReport: "resizeWidthBOMReport",
    resizeDocumentTreeWidth: "resizeDocumentTreeWidth",
    resizeSideNav: "resizeSideNav",
    redirectURL: "redirectURL",
    failedFiles: "failedFiles",
    subTaskTree: "subTaskTree"
};

// To build version string
export const VersionFetchURL = `${window.location.protocol}//${window.location.hostname}${window.location.port ? ":" + window.location.port : ''}/meta.json?v=${+new Date()}`;

// Navbars
export const NavBars = (perm: any) => {

    let module_access = Object.keys(perm);

    const token = localStorage.getItem(LocalStorageKeys.authToken) || "";

    const userData = token ? parseJwt(token) : {};

    const isUserAdmin = userData?.role === L4;

    return [
        {
            id: "1",
            is_active: true,
            title: 'Home',
            path: AppRoutes.home,
            icon: <HomeOutlined fontSize="small" />,
            subNavBar: []
        },
        {
            id: "2",
            is_active: module_access.includes(MainModule.mm),
            title: 'Materials Module',
            routes: [AppRoutes.materialDashboard, AppRoutes.allMaterials, AppRoutes.descriptionGenerator, AppRoutes.descriptionGenerator + "?tab=1", AppRoutes.descriptionGenerator + "?tab=2", AppRoutes.materialRepository],
            icon: <MuseumOutlined fontSize="small" />,
            subNavBar: [
                {
                    id: "2.0",
                    is_active: HasAccess(perm, PERMISSIONS.MATERIAL_DASHBOARD),
                    title: "Dashboard",
                    path: AppRoutes.materialDashboard,
                    subNavBar: []
                },
                {
                    id: "2.1",
                    is_active: HasAccess(perm, PERMISSIONS.MATERIAL_READ),
                    title: "Materials",
                    path: AppRoutes.allMaterials,
                    subNavBar: []
                },
                {
                    id: "2.2",
                    is_active: HasAccess(perm, PERMISSIONS.BOM_READ),
                    title: "BOMs",
                    path: AppRoutes.bom,
                    subNavBar: []
                },
                {
                    id: "2.3",
                    is_active: HasAccess(perm, PERMISSIONS.WORKBOOK_READ),
                    title: "Workbook",
                    path: AppRoutes.materialWorkbook,
                    subNavBar: []
                },
                {
                    id: "2.4",
                    is_active: HasAccess(perm, PERMISSIONS.DG_READ),
                    title: "Description Generator",
                    path: AppRoutes.descriptionGenerator + "?tab=1",
                    subNavBar: []
                },
                {
                    id: "2.5",
                    is_active: HasAccess(perm, PERMISSIONS.MATERIAL_REPO_READ),
                    title: "Repository",
                    path: AppRoutes.materialRepository,
                    subNavBar: []
                }
            ]
        },
        // {
        //     id: "3",
        //     is_active: module_access.includes("prodm"),
        //     title: 'CAD Library',
        //     path: AppRoutes.enquiry,
        //     disabled: true,
        //     icon: <HouseSiding fontSize="small" />,
        //     subNavBar: []
        // },
        {
            id: "3",
            is_active: module_access.includes(MainModule.prodm),
            title: 'Product Module',
            routes: [AppRoutes.productDashboard, AppRoutes.product_vc, AppRoutes.products, AppRoutes.product_repository],
            icon: <LocalMallOutlined fontSize="small" />,
            subNavBar: [
                {
                    id: "3.1",
                    is_active: HasAccess(perm, PERMISSIONS.PRODUCT_DASHBOARD),
                    title: "Dashboard",
                    path: AppRoutes.productDashboard,
                    subNavBar: []
                },
                {
                    id: "3.2",
                    is_active: HasAccess(perm, PERMISSIONS.PRODUCT_READ),
                    title: "Products",
                    path: AppRoutes.products,
                    subNavBar: []
                },
                {
                    id: "3.3",
                    is_active: HasAccess(perm, PERMISSIONS.PRODUCT_VC),
                    title: "Variant Configurator",
                    path: AppRoutes.product_vc,
                    subNavBar: []
                },
                {
                    id: "3.4",
                    is_active: HasAccess(perm, PERMISSIONS.PRODUCT_REPO_READ),
                    title: "Repository",
                    path: AppRoutes.product_repository,
                    subNavBar: []
                },
                // {
                //     id: "4.2",
                //     is_active: module_access.includes("prodm"),
                //     title: "Proposal Configurator",
                //     disabled: true,
                //     path: AppRoutes.pmo_pg,
                //     subNavBar: []
                // }
            ]
        },
        {
            id: "4",
            is_active: module_access.includes(MainModule.propm),
            title: "Proposal Module",
            routes: [AppRoutes.proposals, AppRoutes.proposalsDashboard],
            icon: <AutoFixHighOutlined fontSize="small" />,
            subNavBar: [
                {
                    id: "4.1",
                    is_active: HasAccess(perm, PERMISSIONS.PROPOSALS_DASHBOARD),
                    title: "Dashboard",
                    path: AppRoutes.proposalsDashboard,
                    subNavBar: []
                },
                {
                    id: "4.2",
                    is_active: HasAccess(perm, PERMISSIONS.PROPOSAL_READ),
                    title: "Proposals",
                    path: AppRoutes.proposals,
                    subNavBar: []
                },
            ]
        },
        {
            id: "5",
            is_active: module_access.includes(MainModule.projm),
            title: "Project Module",
            routes: [AppRoutes.projects, AppRoutes.projectDashboard],
            icon: <EngineeringOutlined fontSize="small" />,
            subNavBar: [
                {
                    id: "5.1",
                    is_active: HasAccess(perm, PERMISSIONS.PROJECT_DASHBOARD),
                    title: "Dashboard",
                    path: AppRoutes.projectDashboard,
                    subNavBar: []
                },
                {
                    id: "5.2",
                    is_active: HasAccess(perm, PERMISSIONS.PROJECT_READ),
                    title: "Projects",
                    path: AppRoutes.projects,
                    subNavBar: []
                },
            ]
        },
        {
            id: "6",
            is_active: module_access.includes(MainModule.cm),
            title: "Commercial Module",
            routes: [AppRoutes.cmDashboard, AppRoutes.pricingList, AppRoutes.allQuickQuotes],
            icon: <RequestQuoteOutlined fontSize="small" />,
            subNavBar: [
                {
                    id: "6.1",
                    is_active: HasAccess(perm, PERMISSIONS.COMMERCIAL_DASHBOARD),
                    title: "Dashboard",
                    disabled: true,
                    path: AppRoutes.cmDashboard,
                    subNavBar: []
                },
                {
                    id: "6.2",
                    is_active: HasAccess(perm, PERMISSIONS.COMMERCIAL_PRICING_READ),
                    title: "Pricing",
                    path: AppRoutes.pricingList,
                    subNavBar: [],
                    disabled: !isUserAdmin,
                    tooltipText: isUserAdmin ? "" : "Only L4 user has visibility for pricing dashboard - to get access contact administrator"
                },
                {
                    id: "6.3",
                    is_active: HasAccess(perm, PERMISSIONS.COMMERCIAL_IEQUOTE_READ),
                    title: "Quick Quote",
                    path: AppRoutes.allQuickQuotes,
                    subNavBar: []
                }
            ]
        },
        {
            id: "7",
            is_active: module_access.includes(MainModule.dm),
            title: "Document Module",
            routes: [AppRoutes.docDashboard, AppRoutes.documents, AppRoutes.bomReport, AppRoutes.viewAllDrawioDiagrams, AppRoutes.viewAllDiagramTemplates],
            icon: <SnippetFolderOutlined fontSize="small" />,
            subNavBar: [
                {
                    id: "7.1",
                    is_active: true,
                    title: "Dashboard",
                    path: AppRoutes.docDashboard,
                    subNavBar: []
                },
                {
                    id: "7.2",
                    is_active: true,
                    title: "Documents",
                    path: AppRoutes.documents,
                    subNavBar: []
                },
                {
                    id: "7.3",
                    is_active: false,
                    title: "BOM Report",
                    path: AppRoutes.bomReport,
                    disabled: false,
                    subNavBar: []
                },
                {
                    id: "7.4",
                    is_active: false,
                    title: "IEDRAW",
                    path: AppRoutes.viewAllDrawioDiagrams,
                    disabled: false,
                    isBeta: true,
                    subNavBar: []
                },
                {
                    id: "7.5",
                    is_active: false,
                    title: "IEDRAW Templates",
                    path: AppRoutes.viewAllDiagramTemplates,
                    disabled: false,
                    isBeta: true,
                    subNavBar: []
                },
                // {
                //     id: "8.3",
                //     is_active: true,
                //     title: "Document Builder",
                //     path: AppRoutes.suppliersList,
                //     disabled: true,
                //     subNavBar: []
                // },
                // {
                //     id: "8.4",
                //     is_active: true,
                //     title: "Cutsheet Builder",
                //     path: AppRoutes.cutSheetBuilder,
                //     disabled: false,
                //     subNavBar: []
                // },
                // {
                //     id: "8.5",
                //     is_active: true,
                //     title: "Worksheet",
                //     path: AppRoutes.suppliersList,
                //     disabled: true,
                //     subNavBar: []
                // },
            ]
        },
        {
            id: "8",
            is_active: false,
            title: "Quality Module",
            routes: [AppRoutes.qualityDashboard, AppRoutes.issuesList, AppRoutes.checkList],
            icon: <Verified fontSize="small" />,
            subNavBar: [
                {
                    id: "8.1",
                    is_active: true,
                    title: "Dashboard",
                    disabled: true,
                    path: AppRoutes.qualityDashboard,
                    subNavBar: []
                },
                {
                    id: "8.2",
                    is_active: true,
                    title: "Issue Tracker",
                    path: AppRoutes.issuesList,
                    disabled: false,
                    subNavBar: []
                }
            ]
        },
        {
            id: "9",
            is_active: module_access.includes(MainModule.vm),
            title: "Supplier Module",
            routes: [AppRoutes.vendors, AppRoutes.suppliersList, AppRoutes.vendorsDashboard],
            icon: <ShoppingBasketOutlined fontSize="small" />,
            subNavBar: [
                {
                    id: "9.1",
                    is_active: HasAccess(perm, PERMISSIONS.SUPPLIER_READ),
                    title: "Dashboard",
                    disabled: false,
                    path: AppRoutes.vendorsDashboard,
                    subNavBar: []
                },
                {
                    id: "9.2",
                    is_active: HasAccess(perm, PERMISSIONS.SUPPLIER_READ),
                    title: "Suppliers",
                    path: AppRoutes.vendors,
                    subNavBar: []
                },
                {
                    id: "9.3",
                    is_active: HasAccess(perm, PERMISSIONS.SUPPLIER_READ),
                    title: "Approved Supplier List",
                    path: AppRoutes.suppliersList,
                    subNavBar: []
                }
            ]
        },
        // {
        //     id: "10",
        //     is_active: true,
        //     title: "Knowledge Module",
        //     path: AppRoutes.knowledge,
        //     disabled: true,
        //     icon: <School fontSize="small" />,
        //     subNavBar: []
        // },
        {
            id: "10",
            is_active: module_access.includes(MainModule.ai),
            title: "AI Module",
            routes: [AppRoutes.ai, AppRoutes.aiInstantSearch],
            icon: <PrecisionManufacturingOutlined fontSize="small" />,
            subNavBar: [
                {
                    id: "10.1",
                    is_active: HasAccess(perm, PERMISSIONS.AI_SEARCH_READ),
                    title: "Instant Search",
                    path: AppRoutes.aiInstantSearch,
                    subNavBar: []
                },
                {
                    id: "10.2",
                    is_active: HasAccess(perm, PERMISSIONS.AI_SEARCH_READ),
                    title: "Search Prompts",
                    path: AppRoutes.searchPrompts,
                    subNavBar: []
                },
                // {
                //     id: "12.2",
                //     is_active: true,
                //     title: "Proposal Builder",
                //     disabled: true,
                //     path: AppRoutes.userManagement,
                //     subNavBar: []
                // },
                // {
                //     id: "12.3",
                //     is_active: true,
                //     title: "BOM Builder",
                //     disabled: true,
                //     path: AppRoutes.userManagement,
                //     subNavBar: []
                // },
                // {
                //     id: "12.4",
                //     is_active: true,
                //     title: "Manual Builder",
                //     disabled: true,
                //     path: AppRoutes.userManagement,
                //     subNavBar: []
                // },
                // {
                //     id: "12.5",
                //     is_active: true,
                //     title: "PID Builder",
                //     disabled: true,
                //     path: AppRoutes.userManagement,
                //     subNavBar: []
                // },
                // {
                //     id: "12.6",
                //     is_active: true,
                //     title: "BOT ME",
                //     disabled: true,
                //     path: AppRoutes.userManagement,
                //     subNavBar: []
                // },
                // {
                //     id: "12.7",
                //     is_active: true,
                //     title: "BOT EE",
                //     disabled: true,
                //     path: AppRoutes.userManagement,
                //     subNavBar: []
                // },
            ]
        },
        {
            id: "11",
            is_active: module_access.includes(MainModule.tm),
            title: "Tags & Template Module",
            routes: [AppRoutes.tags, AppRoutes.tagGroups, AppRoutes.viewAllDiagramTemplates, AppRoutes.viewTemplateList, AppRoutes.sequenceNumbering()],
            icon: <SellOutlined fontSize="small" />,
            subNavBar: [
                {
                    id: "11.1",
                    is_active: HasAccess(perm, PERMISSIONS.TAG_CATEGORY_READ),
                    title: "Tag Categories",
                    path: AppRoutes.tagGroups,
                    subNavBar: []
                },
                {
                    id: "11.2",
                    is_active: HasAccess(perm, PERMISSIONS.TAG_READ),
                    title: "Tags",
                    path: AppRoutes.tags,
                    subNavBar: []
                },
                {
                    id: "11.3",
                    is_active: HasAccess(perm, PERMISSIONS.DOCUMENT_TEMPLATE_READ),
                    title: "Document Templates",
                    path: AppRoutes.viewTemplateList,
                    subNavBar: []
                },
                {
                    id: "11.4",
                    is_active: HasAccess(perm, PERMISSIONS.SEQUENCE_NO_TEMPLATE_READ),
                    title: "Sequence Numbering (SN)",
                    path: AppRoutes.sequenceNumbering(MasterPath.category),
                    subNavBar: []
                },
                {
                    id: "11.5",
                    is_active: HasAccess(perm, PERMISSIONS.IEDRAW_TEMPLATE_READ),
                    title: "IEDRAW Templates",
                    path: AppRoutes.viewAllDiagramTemplates,
                    disabled: false,
                    isBeta: true,
                    subNavBar: []
                },
            ]
        },
        {
            id: "12",
            is_active: module_access.includes(MainModule.um),
            title: "User Module",
            routes: [AppRoutes.user, AppRoutes.userManagement, AppRoutes.licenseManagement],
            icon: < VpnKeyRounded fontSize="small" />,
            subNavBar: [
                {
                    id: "12.1",
                    is_active: HasAccess(perm, PERMISSIONS.USER_MODULE_MEM_READ),
                    title: "User Management",
                    path: AppRoutes.userManagement,
                    subNavBar: []
                },
                {
                    id: "12.2",
                    is_active: true,
                    title: "License Management",
                    path: AppRoutes.licenseManagement,
                    subNavBar: [],
                    disabled: true
                },
                {
                    id: "12.3",
                    is_active: HasAccess(perm, PERMISSIONS.USER_MODULE_MEM_READ),
                    title: "Settings",
                    path: AppRoutes.currencySettings,
                    subNavBar: []
                },
            ]
        },
        {
            id: "13",
            is_active: true,
            title: "Help & Support",
            routes: [AppRoutes.tickets],
            icon: <Info fontSize="small" />,
            subNavBar: [
                {
                    id: "13.1",
                    is_active: true,
                    disabled: true,
                    title: "My Tickets",
                    path: AppRoutes.tickets,
                    subNavBar: []
                },
                {
                    id: "13.2",
                    is_active: true,
                    title: "How to?",
                    path: AppRoutes.howTo,
                    subNavBar: [],
                    disabled: true
                }
            ]
        },
    ]
}

export const TagsModuleNavBars = (tagGroups: any): SecondaryNavBars[] => {

    let navBars = [
        {
            id: "all",
            title: "All Tags",
            icon: null,
            path: AppRoutes.tags + "?group=all"
        }
    ]

    if (tagGroups) {
        navBars = [...navBars, ...tagGroups?.map((group: any, index: number) => ({
            id: group._id,
            title: group.tag_group_name,
            icon: null,
            path: `${AppRoutes.tags}?group=${group._id}&name=${group.tag_group_name}`
        }
        ))
        ]
    }

    return navBars;
}

export const TagGroupsModuleNavBars: SecondaryNavBars[] = [
    {
        id: "1",
        title: "Dashboard",
        icon: <Dashboard fontSize="small" />,
        path: AppRoutes.tagGroups
    }
]

export const HorizontalPosition: SelectBoxOption[] = [
    { label: "Left", value: "left" }, { label: "Right", value: "right" }, { label: "Center", value: "center" }
]

export const VerticalPosition: SelectBoxOption[] = [
    { label: "Top", value: "top" }, { label: "Bottom", value: "bottom" }, { label: "Middle", value: "middle" }
]

export const Dimensions: SelectBoxOption[] = [
    { label: "Full", value: "full" }, { label: "Half", value: "half" }
]

export const paginationFormatOptions = [
    "1 of N",
    "1/N",
    "1"
] as const

export const entityHistoryOptions = [{ value: "false", label: "Latest Only" }, { value: "true", label: "All" }]

// For Canonical Names

export const TAG_CANONICAL_NAMES = {
    doc_type: "doc_type",
    folders: "folders",
    cl_general: "cl_general",
    cl_status: "cl_status",
    ds_tg: "ds_tg",
    tech_para: "tech_para",
    business_units: "business_units",
    prod_status: "prod_status",
    prod_general: "prod_general",
    proj_category: "proj_category",
    proj_general: "proj_general",
    proj_status: "proj_status",
    prop_category: "prop_category",
    prop_general: "prop_general",
    prop_status: "prop_status",
    quote_status: "quote_status",
    quote_tg: "quote_tg",
    iss_status: "iss_status",
    iss_tag: "iss_tag",
    iss_category: "iss_category",
    proposal_priority: "proposal_priority",
    project_priority: "project_priority",
    product_priority: "product_priority",
    quote_priority: "quote_priority",
    price_priority: "price_priority",
    bom_priority: "bom_priority",
    material_priority: "material_priority",
    task_category: "task_category",
    task_priority: "task_priority",
    task_type: "task_type",
    label: "label",
    wf_assignment_purpose: "wf_assignment_purpose",
    doc_priority: "doc_priority",
    doc_status: "doc_status",
    sp_category: "sp_category",
    child_bom_status: "child_bom_status",
    child_mat_status: "child_mat_status",
    project_task_workflow: "project_task_workflow",
    proposal_task_workflow: "proposal_task_workflow",
    product_task_workflow: "product_task_workflow",
    project_workflow: "project_workflow",
    product_workflow: "product_workflow",
    proposal_workflow: "proposal_workflow",
    proposal_task_label: "proposal_task_label",
    project_task_label: "project_task_label",
    product_task_label: "product_task_label",
    proposal_task_category: "proposal_task_category",
    project_task_category: "project_task_category",
    product_task_category: "product_task_category",
    proposal_task_priority: "proposal_task_priority",
    project_task_priority: "project_task_priority",
    product_task_priority: "product_task_priority",
    proposal_task_type: "proposal_task_type",
    project_task_type: "project_task_type",
    product_task_type: "product_task_type",
} as const;

export const LIGHT_COLORS = [
    "#FFCDD2", // Light Red
    "#F8BBD0", // Light Pink
    "#E1BEE7", // Light Purple
    "#D1C4E9", // Lavender
    "#C5CAE9", // Soft Blue
    "#BBDEFB", // Baby Blue
    "#B3E5FC", // Sky Blue
    "#B2EBF2", // Light Cyan
    "#B2DFDB", // Mint Green
    "#C8E6C9", // Pastel Green
    "#DCEDC8", // Lime Green
    "#FFF9C4", // Pale Yellow
    "#FFECB3", // Peach
    "#FFE0B2", // Apricot
    "#FFCCBC"  // Light Coral
];

// Root folder name
export const ROOT_FOLDER_NAME = 'All Files';

// default pagination options for board view
export const DEFAULT_BOARD_VIEW_PAGINATION = { page: 0, pageSize: 100 };

// Create the toggle button plugin for suneditor
const collapseIcon = `<svg viewBox="0 0 24 24" style="width:16px; height:16px;" fill="currentColor">
  <path d="M5 9l7 7 7-7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`;

const moreIcon = `
            <svg viewBox="0 0 24 24" style="width:16px; height:16px;">
               <circle cx="5" cy="12" r="2" fill="currentColor"/>
               <circle cx="12" cy="12" r="2" fill="currentColor"/>
               <circle cx="19" cy="12" r="2" fill="currentColor"/>
            </svg>
          `;

// Create the toggle button plugin for suneditor
export const togglePlugin = (toggleFn: () => void, isExpanded: boolean) => {
    return {
        name: "toggleButton", // This name will be used in the buttonList array.
        display: "command",   // Indicates this is a toolbar command.
        title: isExpanded ? "Less Options" : "More Options",        // Tooltip text.
        innerHTML: isExpanded ? collapseIcon : moreIcon,
        add: () => { },
        action: function () {
            toggleFn();
        }
    };
};

export const allSunEditorPlugins = [
    align,
    font,
    fontSize,
    fontColor,
    hiliteColor,
    horizontalRule,
    list,
    paragraphStyle,
    table,
    textStyle,
    image,
    link,
    video,
    audio,
    math,
    mention,
    blockquote
];